import {
  DESTINATION_OPTIONS,
  INCIDENT_IO,
  INTEGRATION_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IncidentIoIntegrationForm } from 'shared/types';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import useSnackbar from '../useSnackbar';
import * as api from '../../services/settingsApi';
import useSendTracking from './useSendTracking';

type Props = {
  onUpdateSuccess?: () => void;
};

const useIncidentIoIntegration = ({ onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const incidentIoQuery = useQuery(
    [INCIDENT_IO],
    () => api.fetchIncidentIo(),
    {},
  );

  const incidentIoIntegrationMutation = useMutation(
    (data: IncidentIoIntegrationForm) =>
      incidentIoQuery.data?.integrated
        ? api.updateIncidentIoIntegration({ sources: data.sources })
        : api.setIncidentIoIntegration({ sources: data.sources }),
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([INCIDENT_IO]);
        queryClient.invalidateQueries([DESTINATION_OPTIONS]);
        queryClient.invalidateQueries([INTEGRATION_OPTIONS]);
        openSnackbar({
          message: 'Successfully connected to your incident.io',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName:
            trackingEvents?.CONNECT_INCIDENT_IO_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_INCIDENT_IO_INTEGRATION_FAILURE,
        }),
    },
  );

  return {
    incidentIoQuery,
    incidentIoIntegrationMutation,
    isIncidentIoConnected: !!incidentIoQuery.data?.integrated,
  };
};

export default useIncidentIoIntegration;
